// Rating.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { NULL_FUNCTION } from '../resource/defaults.js';
import { ButtonId } from '../resource/mixpanel.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';

import StarGrayFillIconSource from '../../img/ic-star-gray-fill.svg';
import StarHalfFillIconSource from '../../img/ic-star-half-fill.svg';
import StarFillIconSource from '../../img/ic-star-fill.svg';

const getIconSource = (value, requirement) => {
  const gap = +(requirement - value).toFixed(2);
  if (value >= requirement || gap <= 0.2) {
    return StarFillIconSource;
  }
  if (gap >= 0.3 && gap <= 0.7) {
    return StarHalfFillIconSource;
  }
  return StarGrayFillIconSource;
};

export const Rating = ({
  trackingModalId = null,
  value = 0,
  max = 0,
  iconSize = 20,
  iconGap = 0,
  isReadOnly = false,
  onChange = NULL_FUNCTION,
}) => {
  const requirements = useMemo(
    () => new Array(max).fill('').map((_, index) => index + 1),
    [max]
  );
  return (
    <StyledRating>
      {requirements.map(requirement => {
        const IconComponent = (
          <img
            src={getIconSource(value, requirement)}
            width={iconSize}
            height={iconSize}
            alt="rating"
          />
        );
        if (isReadOnly) {
          return (
            <RatingIcon key={requirement} size={iconSize} iconGap={iconGap}>
              {IconComponent}
            </RatingIcon>
          );
        }
        return (
          <ClickableRatingIcon
            key={requirement}
            data-element_id={ButtonId.All.ButtonRatingStar}
            data-tracking_payload={{
              modalId: trackingModalId,
              'rating.star': requirement,
            }}
            size={iconSize}
            iconGap={iconGap}
            onClick={() => onChange({ value: requirement })}
          >
            {IconComponent}
          </ClickableRatingIcon>
        );
      })}
    </StyledRating>
  );
};

Rating.propTypes = {
  trackingModalId: PropTypes.string,
  value: PropTypes.number,
  max: PropTypes.number,
  iconSize: PropTypes.number,
  iconGap: PropTypes.number,
  isReadOnly: PropTypes.bool,
  onChange: PropTypes.func,
};

const StyledRating = styled.div`
  display: inline-flex;
  align-items: center;
`;

const RatingIconStyle = css`
  margin-left: ${({ iconGap }) => iconGap}px;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  display: inline-flex;
  align-items: center;
  :first-child {
    margin-left: 0;
  }
`;

const RatingIcon = styled.div`
  ${RatingIconStyle};
`;

const ClickableRatingIcon = styled.button`
  ${ResetButtonStyle};
  ${RatingIconStyle};
`;

export default Rating;
